import { useEffect, useRef, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';

export interface ReferralStats {
  code: string | null;
  conversionsCount: number;
  totalEarnings: number;
  pendingEarnings: number;
  paidEarnings: number;
}

interface ReferralEvent {
  type: 'initial' | 'referral_updated';
  stats: ReferralStats;
}

/**
 * Hook to subscribe to real-time referral updates via SSE
 * Returns stats that update automatically when conversions/payments occur
 */
export function useReferralEvents(onUpdate?: (stats: ReferralStats) => void) {
  const { token, isAuthenticated } = useAuthStore();
  const eventSourceRef = useRef<EventSource | null>(null);
  const onUpdateRef = useRef(onUpdate);

  // Keep callback ref updated
  onUpdateRef.current = onUpdate;

  const connect = useCallback(() => {
    if (!isAuthenticated || !token) {
      return;
    }

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const eventSource = new EventSource(`/api/v1/referrals/events?token=${token}`);
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data: ReferralEvent = JSON.parse(event.data);

        if (data.type === 'initial' || data.type === 'referral_updated') {
          onUpdateRef.current?.(data.stats);
        }
      } catch (error) {
        console.error('Failed to parse referral SSE message:', error);
      }
    };

    eventSource.onerror = () => {
      // EventSource will automatically try to reconnect
    };
  }, [token, isAuthenticated]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [connect]);

  return {
    reconnect: connect,
  };
}
